/**
 * Errors the platform crypto providers throw when the ENVIRONMENT fails, as
 * distinct from "no key stored", which is `null`.
 *
 * The distinction is load-bearing. `encryptionStore.initialize` turns `null`
 * into `hasLocalKey: false`, and the root layout turns that into the
 * recovery-phrase screen. Collapsing a storage failure into `null` — which
 * crypto.web.ts did on three separate paths — put users in front of a 24-word
 * form for a key that was still sitting on the device.
 *
 * Platform-neutral on purpose: both providers import from here, so `instanceof`
 * means the same thing whichever one Metro bundled, and jest can import it
 * without resolving a platform file.
 */

/**
 * Key storage itself could not be used — IndexedDB refused, a transaction
 * failed, the secure store errored, or a stored record is unreadable.
 */
export class StorageUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StorageUnavailableError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * The cryptographic primitives are missing. On the web that means
 * `crypto.subtle` is undefined, which in practice means the page is not on a
 * secure origin. `crypto.getRandomValues` is NOT gated the same way, so phrase
 * generation succeeds and the failure surfaces only at derivation — where it
 * used to read as a wrong recovery phrase or a bad connection.
 */
export class CryptoUnavailableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CryptoUnavailableError';
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** True for either environment error. Their messages are written to be shown to the user as-is. */
export function isCryptoEnvironmentError(
  err: unknown
): err is StorageUnavailableError | CryptoUnavailableError {
  return err instanceof StorageUnavailableError || err instanceof CryptoUnavailableError;
}
